import React, { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate } from 'react-router-dom';
import { gql } from 'graphql-request';
import { useMutation } from '@hooks/graphql'
import MethodSelectionStep from './components/MethodSelectionStep';
import FileUploadStep from './components/FileUploadStep';
import PathwayTypeStep from './components/PathwayTypeStep';
import ManualCreationStep from './components/ManualCreationStep';
import LoadingStep from './components/LoadingStep';

// Define state names
const STEPS = {
    METHOD_SELECTION: 'METHOD_SELECTION',
    AI_FILE_UPLOAD: 'AI_FILE_UPLOAD',
    AI_PATHWAY_TYPE: 'AI_PATHWAY_TYPE',
    AI_GENERATING: 'AI_GENERATING', // Represents the loading state for AI
    MANUAL_FORM: 'MANUAL_FORM',     // Represents the form input state for manual
};

// --- New GraphQL Mutation for AI Generation ---
const CREATE_GENERATION_MUTATION = gql`
  mutation createGeneration($input: CreateGenerationInput!) {
    createGeneration(input: $input) {
      educatorProjectGeneration {
        id
      },
      errors {
        message
      }
    }
  }
`;

const CreateLesson = ({ seriesName, seriesId, onFinished }) => {
    // --- Hooks ---  
    const manualStepRef = useRef();
    const navigate = useNavigate(); // For redirecting after creation

    const [currentState, setCurrentState] = useState(STEPS.METHOD_SELECTION);
    const [selectedMethod, setSelectedMethod] = useState('ai');

    const [uploadedFile, setUploadedFile] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');

    const [selectedPathwayType, setSelectedPathwayType] = useState(null);
    const [validationError, setValidationError] = useState('');

    const [generationId, setGenerationId] = useState(null);

    // --- New Mutation for AI Generation ---
    const { mutateAsync: createGeneration, isLoading: isCreating } = useMutation({
        gqlMutation: CREATE_GENERATION_MUTATION,
        onSuccess: (data) => {
            const generation = data?.createGeneration?.educatorProjectGeneration;
            if (generation) {
                setGenerationId(generation.id);
            } else {
                const errorMessage = data?.createGeneration?.errors?.[0]?.message || "Failed to start lesson generation. Please try again later or contact our support if the issue persists.";
                setValidationError(errorMessage);
            }
        },
        onError: (error) => {
            setIsLoading(false);
            const message = error?.response?.errors?.[0]?.message || "Failed to start lesson generation. Please try again.";
            setValidationError(message);
        }
    });

    // --- Handlers ---
    const handleMethodSelect = useCallback((method) => {
        console.log('Method selected:', method);
        setSelectedMethod(method);
        // Reset subsequent steps' data if method changes after the first step
        if (currentState !== STEPS.METHOD_SELECTION) {
            // Reset data associated with the previous path
            setUploadedFile(null);
            setSelectedPathwayType(null);
            // Optionally reset to the first step or let user navigate back manually
            // setCurrentState(STEPS.METHOD_SELECTION); // Consider if this is desired UX
        }
        setValidationError('');
    }, [currentState]);

    // Updated to handle a single file object or null
    const handleUploadSuccess = useCallback((selectedFile) => {
        if (selectedFile) {
            console.log('File processed by parent:', selectedFile);
            setUploadedFile(selectedFile); // Store the single file object
            setValidationError('');

            setCurrentState(STEPS.AI_PATHWAY_TYPE);
        } else {
            console.log('File removed notification via success handler (unexpected)');
            setUploadedFile(null);
        }
    }, []);

    // Handler for removing the file, called by FileUploadStep's onFileRemove prop
    const handleFileRemove = useCallback((removedFileId) => {
        setUploadedFile(null); // Clear the state in the parent
        setValidationError('');
    }, []);

    const handleTypeSelect = (typeObject) => {
        setSelectedPathwayType(typeObject);
        setValidationError('');
    };

    const startGeneratingLesson = async (file, pathwayType, seriesId) => {
        try {
            setIsLoading(true); // Start loading indicator/animation display
            await createGeneration({
                input: {
                    seriesId: seriesId,
                    lessonType: pathwayType,
                    files: [file]
                }
            });
        } catch (error) {
            setValidationError("Oops! Something funky happened while creating your lesson. Please try again or contact support if this persists.");
        } finally {
            setIsLoading(false); // Stop loading indicator
        }
    }

    const handleNext = async () => {
        setValidationError('');

        switch (currentState) {
            case STEPS.METHOD_SELECTION:
                setCurrentState(selectedMethod === 'ai' ? STEPS.AI_FILE_UPLOAD : STEPS.MANUAL_FORM);
                break;

            case STEPS.AI_FILE_UPLOAD:
                if (!uploadedFile) {
                    setValidationError("Please upload a file before proceeding.");
                    return;
                }
                setCurrentState(STEPS.AI_PATHWAY_TYPE);
                break;

            case STEPS.AI_PATHWAY_TYPE:
                if (!selectedPathwayType) {
                    setValidationError("Please select the type of lesson you want to create.");
                    return;
                }

                startGeneratingLesson(uploadedFile, selectedPathwayType.id.toUpperCase(), seriesId);

                setCurrentState(STEPS.AI_GENERATING);

                break;

            case STEPS.MANUAL_FORM:
                setLoadingText('Creating...');
                setIsLoading(true);
                try {
                    // Trigger the save action in the ManualCreationStep
                    // Assume it handles success/error feedback and navigation/onFinished internally.
                    await manualStepRef.current.createLesson();
                    // If createLesson resolves, assume success and it handled the finish/redirect.
                    // We don't change parent state here. Loading will be reset by createLesson or catch block.
                } catch (error) {
                    console.error("Manual creation failed:", error);
                    setValidationError(error.message || "Failed to create lesson.");
                    setIsLoading(false); // Stop loading on error
                }
                break;

            // No 'Next' action from AI_GENERATING state
            case STEPS.AI_GENERATING:
                // This case should ideally not be reached if the button is disabled,
                // but it's good practice to handle it.
                break;

            default:
                console.error("Unknown state:", currentState);
        }
    };

    const handleBack = () => {
        // Prevent back during critical async operations
        if (isLoading && (currentState === STEPS.AI_GENERATING || (currentState === STEPS.MANUAL_FORM && isLoading))) {
            return;
        }

        setValidationError(''); // Clear errors when navigating back

        switch (currentState) {
            case STEPS.AI_FILE_UPLOAD:
            case STEPS.MANUAL_FORM:
                setCurrentState(STEPS.METHOD_SELECTION);
                break;
            case STEPS.AI_PATHWAY_TYPE:
                setCurrentState(STEPS.AI_FILE_UPLOAD);
                break;
            case STEPS.AI_GENERATING:
                // Handle navigating back from the loading/generating state
                setIsLoading(false); // Stop showing loading indicators
                setGenerationId(null); // Clear generation ID
                // Reset potential errors shown on the loading screen
                setValidationError('');
                setCurrentState(STEPS.AI_PATHWAY_TYPE); // Go back to the previous input step
                break;
            // Cannot go back from the first step
            case STEPS.METHOD_SELECTION:
            default:
                break; // Do nothing
        }
    };

    // --- Conditional Rendering Logic ---
    const showFooter = currentState !== STEPS.AI_GENERATING;
    const showError = currentState !== STEPS.AI_GENERATING && validationError;

    return (
        <div className="w-full min-h-[600px] max-h-[80vh] flex flex-col">

            {/* Main content area - Direct Conditional Rendering */}
            <div className="flex-1 overflow-y-auto mb-4 pr-2 relative flex flex-col">
                {/* Step 1: Method Selection */}
                {currentState === STEPS.METHOD_SELECTION && (
                    <MethodSelectionStep
                        selectedMethod={selectedMethod}
                        onMethodSelect={handleMethodSelect}
                    />
                )}

                {/* Step 2 (AI): File Upload */}
                {currentState === STEPS.AI_FILE_UPLOAD && (
                    <FileUploadStep
                        file={uploadedFile}
                        onFileSelected={handleUploadSuccess}
                        onFileRemove={handleFileRemove}
                    />
                )}

                {/* Step 2 (Manual): Manual Form */}
                {currentState === STEPS.MANUAL_FORM && (
                    <ManualCreationStep
                        id={seriesId}
                        ref={manualStepRef}
                    />
                )}

                {/* Step 3 (AI): Pathway Type */}
                {currentState === STEPS.AI_PATHWAY_TYPE && (
                    <PathwayTypeStep
                        selectedType={selectedPathwayType}
                        onTypeSelect={handleTypeSelect}
                    />
                )}

                {/* Step 4 (AI): Generating */}
                {currentState === STEPS.AI_GENERATING && (
                    <LoadingStep
                        generationId={generationId}
                        onClose={onFinished}
                        onComplete={(educatorProject) => {
                            navigate(`/educators/projects/${educatorProject.id}`);
                        }}
                        errorMessage={validationError}
                    />
                )}
            </div>

            {/* Error Message Area */}
            <If condition={showError}>
                <div className="mt-2 mb-2 text-red-600 text-md font-normal">
                    {validationError}
                </div>
            </If>

            {/* Footer section */}
            <If condition={showFooter}>
                <Footer
                    currentState={currentState}
                    isLoading={isLoading}
                    loadingText={loadingText}
                    selectedMethod={selectedMethod}
                    onBack={handleBack}
                    onNext={handleNext}
                />
            </If>
        </div>
    );
};

// --- WizardFooter Component ---
const Footer = ({
    currentState,
    isLoading,
    loadingText,
    selectedMethod,
    onBack,
    onNext
}) => {
    const disableButtons = isLoading;

    // Determine if the current step is the final *input* step before an action (generate/create)
    const isFinalInputStep = (selectedMethod === 'ai' && currentState === STEPS.AI_PATHWAY_TYPE) ||
        (selectedMethod === 'manual' && currentState === STEPS.MANUAL_FORM);

    // Determine button text based on the step and loading state
    let nextButtonText = 'Next';
    if (isFinalInputStep) {
        nextButtonText = selectedMethod === 'ai' ? 'Generate Lesson' : 'Create Lesson';
    }
    // Override with loading text if currently loading
    if (isLoading) {
        nextButtonText = loadingText;
    }

    // Show back button unless on the very first step
    const showBackButton = currentState !== STEPS.METHOD_SELECTION;

    return (
        <div className="mt-auto pt-4 border-t flex justify-between">
            {/* Back Button */}
            {showBackButton ? (
                <Button type="button" variant="outline" onClick={onBack} disabled={disableButtons}>Back</Button>
            ) : (<div />)} {/* Placeholder to keep alignment */}

            {/* Next / Generate / Create Button */}
            <Button
                type="button"
                onClick={onNext}
                disabled={disableButtons}
            // Keep default variant, or customize if needed
            >
                {nextButtonText}
            </Button>
        </div>
    );
};

export default CreateLesson; 